/**
 * BacktestForm.tsx
 *
 * Input form component for backtest parameters.
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BacktestRequest, DeltaType, DteRangeType } from '../../types/backtest';

interface BacktestFormProps {
  onSubmit: (request: BacktestRequest) => void;
  loading: boolean;
}

const BacktestForm: React.FC<BacktestFormProps> = ({ onSubmit, loading }) => {
  const [searchParams] = useSearchParams();
  const urlTicker = searchParams.get('ticker');

  const [symbol, setSymbol] = useState<string>(urlTicker?.toUpperCase() || 'AAPL');
  const [startDate, setStartDate] = useState<string>('2025-07-01');
  const [endDate, setEndDate] = useState<string>('2025-12-31');

  // Sync symbol with URL parameter when it changes
  useEffect(() => {
    if (urlTicker) {
      setSymbol(urlTicker.toUpperCase());
    }
  }, [urlTicker]);
  const [initialCapital, setInitialCapital] = useState<number>(100000);
  const [cashYieldRate, setCashYieldRate] = useState<number>(4);
  const [entryBehavior, setEntryBehavior] = useState<'auto_enter' | 'trend_filtered' | 'compare'>('compare');
  const [hullBuffer, setHullBuffer] = useState<'no_buffer' | 'with_buffer' | 'compare'>('compare');
  const [selectedDeltas, setSelectedDeltas] = useState<DeltaType[]>(['balanced']);
  const [selectedDteRanges, setSelectedDteRanges] = useState<DteRangeType[]>(['monthly']);

  const toggleDelta = (delta: DeltaType) => {
    setSelectedDeltas(prev => {
      if (prev.includes(delta)) {
        // Don't allow deselecting all
        if (prev.length === 1) return prev;
        return prev.filter(d => d !== delta);
      }
      return [...prev, delta];
    });
  };

  const toggleDteRange = (dte: DteRangeType) => {
    setSelectedDteRanges(prev => {
      if (prev.includes(dte)) {
        // Don't allow deselecting all
        if (prev.length === 1) return prev;
        return prev.filter(d => d !== dte);
      }
      return [...prev, dte];
    });
  };

  // Calculate total combinations including compare modes
  const totalCombinations = useMemo(() => {
    const entryModes = entryBehavior === 'compare' ? 2 : 1;
    const hullModes = hullBuffer === 'compare' ? 2 : 1;
    return selectedDeltas.length * selectedDteRanges.length * entryModes * hullModes;
  }, [selectedDeltas.length, selectedDteRanges.length, entryBehavior, hullBuffer]);

  // Popular symbols for quick selection
  const popularSymbols = [
    'AAPL', 'MSFT', 'NVDA', 'TSLA', 'META', 'AMZN', 'GOOGL',  // Big Tech
    'PLTR', 'COIN', 'HOOD', 'SOFI', 'RDDT', 'MSTR',            // High IV / Meme
    'SPY', 'QQQ'                                                // ETFs
  ];

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();

    const request: BacktestRequest = {
      symbol: symbol.toUpperCase(),
      start_date: startDate,
      end_date: endDate,
      initial_capital: initialCapital,
      cash_yield_rate: cashYieldRate / 100, // Convert percentage to decimal
      entry_behavior: entryBehavior,
      hull_buffer: hullBuffer,
      deltas: selectedDeltas,
      dte_ranges: selectedDteRanges
    };

    onSubmit(request);
  }, [symbol, startDate, endDate, initialCapital, cashYieldRate, entryBehavior, hullBuffer, selectedDeltas, selectedDteRanges, onSubmit]);

  const entryOptions: { value: 'auto_enter' | 'trend_filtered' | 'compare'; label: string; sub: string }[] = [
    { value: 'auto_enter', label: 'No', sub: 'Always enter' },
    { value: 'trend_filtered', label: 'Yes', sub: 'Filter entries' },
    { value: 'compare', label: 'Compare', sub: 'Run both' }
  ];

  const hullOptions: { value: 'no_buffer' | 'with_buffer' | 'compare'; label: string; sub: string }[] = [
    { value: 'no_buffer', label: 'No', sub: 'Trigger at breakeven' },
    { value: 'with_buffer', label: 'Yes', sub: 'Trigger at -2%' },
    { value: 'compare', label: 'Compare', sub: 'Run both' }
  ];

  const deltaOptions: { value: DeltaType; label: string; sub: string }[] = [
    { value: 'defensive', label: 'Defensive', sub: 'Delta 0.18' },
    { value: 'balanced', label: 'Balanced', sub: 'Delta 0.25' },
    { value: 'income', label: 'Income', sub: 'Delta 0.35' }
  ];

  const dteOptions: { value: DteRangeType; label: string; sub: string }[] = [
    { value: 'weekly', label: 'Weekly', sub: '5-12 days' },
    { value: 'biweekly', label: 'Bi-weekly', sub: '14-30 days' },
    { value: 'monthly', label: 'Monthly', sub: '30-45 days' }
  ];

  return (
    <form onSubmit={handleSubmit} className="bt-form">
      <div className="bt-panel-h">
        <h2 className="bt-h">Backtest parameters</h2>
      </div>

      {/* Symbol Selection */}
      <div className="bt-row">
        <label className="bt-row-l" htmlFor="bt-symbol">Stock Symbol</label>
        <div className="bt-row-c">
          <input
            id="bt-symbol"
            className="bt-input is-sym"
            type="text"
            value={symbol}
            onChange={(e) => setSymbol(e.target.value.toUpperCase())}
            placeholder="AAPL"
            aria-label="Enter symbol (e.g., AAPL)"
            required
            maxLength={10}
          />
          <div className="bt-chips">
            {popularSymbols.map((s) => (
              <button
                key={s}
                type="button"
                className="bt-chip"
                aria-pressed={symbol === s}
                onClick={() => setSymbol(s)}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Date Range */}
      <div className="bt-row">
        <span className="bt-row-l">Period</span>
        <div className="bt-row-c">
          <label className="bt-field">
            <span>Start Date</span>
            <input
              type="date"
              className="bt-input is-date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              required
            />
          </label>
          <label className="bt-field">
            <span>End Date</span>
            <input
              type="date"
              className="bt-input is-date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              required
            />
          </label>
        </div>
      </div>

      {/* Capital & Yield */}
      <div className="bt-row">
        <span className="bt-row-l">Capital</span>
        <div className="bt-row-c">
          <label className="bt-field">
            <span>Initial Capital ($)</span>
            <input
              type="number"
              className="bt-input"
              value={initialCapital}
              onChange={(e) => setInitialCapital(Number(e.target.value))}
              min={1000}
              max={10000000}
              step={1000}
              required
            />
          </label>
          <label className="bt-field">
            <span>Cash Yield Rate (%)</span>
            <input
              type="number"
              className="bt-input"
              value={cashYieldRate}
              onChange={(e) => setCashYieldRate(Number(e.target.value))}
              min={0}
              max={10}
              step={0.5}
              required
            />
            <small>Annual yield on uninvested cash</small>
          </label>
        </div>
      </div>

      {/* Entry Filter */}
      <div className="bt-row">
        <span className="bt-row-l">
          Use Entry Filter?
          <small>Price {'>'} MA-200 and RSI 30-80</small>
        </span>
        <div className="bt-row-c">
          <div className="seg" role="group" aria-label="Use Entry Filter?">
            {entryOptions.map((o) => (
              <button
                key={o.value}
                type="button"
                aria-pressed={entryBehavior === o.value}
                onClick={() => setEntryBehavior(o.value)}
              >
                {o.label}<small>{o.sub}</small>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Hull Buffer */}
      <div className="bt-row">
        <span className="bt-row-l">
          Hull Breach Buffer?
          <small>2% below breakeven</small>
        </span>
        <div className="bt-row-c">
          <div className="seg" role="group" aria-label="Hull Breach Buffer?">
            {hullOptions.map((o) => (
              <button
                key={o.value}
                type="button"
                aria-pressed={hullBuffer === o.value}
                onClick={() => setHullBuffer(o.value)}
              >
                {o.label}<small>{o.sub}</small>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Delta Selection */}
      <div className="bt-row">
        <span className="bt-row-l">
          Target Delta
          <small>Pick one or more</small>
        </span>
        <div className="bt-row-c">
          <div className="seg" role="group" aria-label="Target Delta">
            {deltaOptions.map((o) => (
              <button
                key={o.value}
                type="button"
                aria-pressed={selectedDeltas.includes(o.value)}
                onClick={() => toggleDelta(o.value)}
              >
                {o.label}<small>{o.sub}</small>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* DTE Range Selection */}
      <div className="bt-row">
        <span className="bt-row-l">
          DTE Range
          <small>Days to Expiration, one or more</small>
        </span>
        <div className="bt-row-c">
          <div className="seg" role="group" aria-label="DTE Range (Days to Expiration)">
            {dteOptions.map((o) => (
              <button
                key={o.value}
                type="button"
                aria-pressed={selectedDteRanges.includes(o.value)}
                onClick={() => toggleDteRange(o.value)}
              >
                {o.label}<small>{o.sub}</small>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Submit */}
      <div className="bt-foot">
        <div className="bt-combos">
          <b>{totalCombinations}</b> combination{totalCombinations !== 1 ? 's' : ''} will be tested
          {(entryBehavior === 'compare' || hullBuffer === 'compare') && (
            <span>
              {' '}({selectedDeltas.length} delta{selectedDeltas.length !== 1 ? 's' : ''} x {selectedDteRanges.length} DTE{selectedDteRanges.length !== 1 ? 's' : ''}
              {entryBehavior === 'compare' && ' x 2 entry'}
              {hullBuffer === 'compare' && ' x 2 hull'})
            </span>
          )}
        </div>
        <button type="submit" className="bt-run" disabled={loading}>
          {loading ? (
            <>
              <span className="bt-spin" aria-hidden="true" />
              Running Backtest...
            </>
          ) : (
            <>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <polygon points="5 3 19 12 5 21 5 3" />
              </svg>
              Run Backtest
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default BacktestForm;
